import {
  Identifier,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
  QuestionnaireResponseItemAnswer,
} from 'fhir/r4';
import { FieldValues } from 'react-hook-form';
import {
  AssessmentsAnswersResponse,
  AssessmentsCheckboxQuestionViewModel,
  AssessmentsQuestionViewModel,
  AssessmentsQuestionViewModels,
  AssessmentsRadioQuestionViewModel,
  AssessmentsSelectQuestionViewModel,
  AssessmentStatus,
  QuestionIdToQuestionNumberMap,
} from '../types';

type GetItemForQuestionProps = {
  question: AssessmentsQuestionViewModel;
  answer?: string | string[];
  answers: FieldValues;
  isSubAnswer?: boolean;
};

const getAnswerExtension = (
  question: AssessmentsQuestionViewModel,
): QuestionnaireResponseItemAnswer['extension'] => [
  {
    url: 'https://healthedge.com/questionnaire-response-answer-metadata',
    extension: [
      {
        url: 'optionId',
        valueInteger: question.optionId,
      },
    ],
  },
];

const getTextAnswer = ({
  question,
  answer,
  isSubAnswer = false,
}: GetItemForQuestionProps): QuestionnaireResponseItem | null => {
  if (!answer) {
    return null;
  }

  const valueKey = question.type === 'date' ? 'valueDate' : 'valueString';
  const answerItem: QuestionnaireResponseItem = {
    linkId: question.id.toString(),
    text: question.questionText,
    answer: [
      {
        [valueKey]: answer,
      },
    ],
  };

  if (!isSubAnswer && answerItem.answer) {
    answerItem.answer[0].extension = getAnswerExtension(question);
  }
  return answerItem;
};

const getLabelAnswer = (
  question: AssessmentsQuestionViewModel,
): QuestionnaireResponseItem | null => {
  const answerItem: QuestionnaireResponseItem = {
    linkId: question.id.toString(),
    text: question.questionText,
    answer: [
      {
        valueString: question.placeholder || question.questionText,
        extension: getAnswerExtension(question),
      },
    ],
  };
  return answerItem;
};

const getChoiceAnswer = ({
  question,
  answerValue,
  getAnswerItem,
  answers,
}: {
  question:
    | AssessmentsRadioQuestionViewModel
    | AssessmentsCheckboxQuestionViewModel
    | AssessmentsSelectQuestionViewModel;
  answerValue?: string | string[];
  getAnswerItem: (props: GetItemForQuestionProps) => QuestionnaireResponseItem | null;
  answers: FieldValues;
}): QuestionnaireResponseItem | null => {
  if (!answerValue || (Array.isArray(answerValue) && answerValue.length === 0)) {
    return null;
  }

  const selectedOptions = typeof answerValue === 'string' ? [answerValue] : answerValue;

  const subQuestionsAnswer = question.options
    .filter((option) => selectedOptions?.includes(option.value) && option.subQuestions)
    .flatMap(
      (option) =>
        option.subQuestions?.map((subQuestion) =>
          getAnswerItem({
            question: subQuestion,
            answer: answers[subQuestion.id.toString()],
            answers,
            isSubAnswer: true,
          }),
        ) ?? [],
    )
    .filter((item) => item !== null);

  let answerItem: QuestionnaireResponseItem = {
    linkId: question.id.toString(),
    text: question.questionText,
    answer: selectedOptions?.map((option) => ({
      valueCoding: {
        system: 'https://healthedge.com/internal-options',
        code: option,
        display: question.options.find((opt) => opt.value === option)?.label,
      },
    })),
  };

  if (subQuestionsAnswer.length) {
    answerItem = {
      ...answerItem,
      item: subQuestionsAnswer,
    };
  }

  return answerItem;
};

const getAnswerItemForQuestion = ({
  question,
  answer,
  isSubAnswer = false,
  answers,
}: GetItemForQuestionProps): QuestionnaireResponseItem | null => {
  switch (question.type) {
    case 'radio':
    case 'checkbox':
    case 'select':
      return getChoiceAnswer({
        question,
        answerValue: answer,
        answers,
        getAnswerItem: getAnswerItemForQuestion,
      });
    case 'textbox':
    case 'textarea':
    case 'date':
      return getTextAnswer({ question, answer, answers, isSubAnswer });
    case 'label':
      return getLabelAnswer(question);
    default:
      return null;
  }
};

export const formatToQuestionnaireResponse = ({
  questions,
  assessmentsId,
  clientPatientId,
  status,
  activityId,
  answers,
  skippedQuestionNumbers = [],
  isSubAnswer = false,
}: {
  questions: AssessmentsQuestionViewModels;
  assessmentsId: string;
  clientPatientId: string;
  status: AssessmentStatus;
  activityId: string;
  answers: FieldValues;
  skippedQuestionNumbers?: number[];
  isSubAnswer?: boolean;
}): QuestionnaireResponse => {
  const item: QuestionnaireResponseItem[] = [];

  questions
    .filter((question) => !skippedQuestionNumbers.includes(question.questionNumber))
    .forEach((question) => {
      const answerItem = getAnswerItemForQuestion({
        question,
        answer: answers[question.id.toString()],
        isSubAnswer,
        answers,
      });

      if (answerItem) {
        item.push(answerItem);
      }
    });

  const questionnaireResponse: QuestionnaireResponse = {
    resourceType: 'QuestionnaireResponse',
    status,
    questionnaire: assessmentsId,
    subject: {
      reference: clientPatientId,
    },
    identifier: {
      system: 'https://healthedge.com/activity-id',
      value: activityId,
    },
    extension: [
      {
        url: 'https://healthedge.com/questionnaire-response-metadata',
        extension: [
          {
            url: 'performedOn',
            valueDateTime: new Date().toISOString(),
          },
          {
            url: 'performedBy',
            valueString: 'Member',
          },
        ],
      },
    ],
    item,
  };

  return questionnaireResponse;
};

const answerToFormValue = ({
  question,
  responseItem,
}: {
  question: AssessmentsQuestionViewModel | undefined;
  responseItem: QuestionnaireResponseItem;
}): string | string[] | undefined => {
  const { answer } = responseItem;

  switch (question?.type) {
    case 'checkbox': {
      const answerValues: string[] = [];
      answer?.forEach((ans) => {
        if (ans.valueCoding?.code) {
          answerValues.push(ans.valueCoding.code);
        }
      });
      return answerValues;
    }
    case 'radio':
    case 'select':
      return answer?.[0]?.valueCoding?.code ?? undefined;
    case 'date':
      return answer?.[0]?.valueDate ?? undefined;
    case 'textbox':
    case 'textarea':
      return answer?.[0]?.valueString ?? undefined;
    default:
      return undefined;
  }
};

const getSubQuestionsMap = (
  question: AssessmentsQuestionViewModel | undefined,
  value: string | string[] | undefined,
): Record<string, AssessmentsQuestionViewModel> => {
  const subQuestionsMap: Record<string, AssessmentsQuestionViewModel> = {};

  if (question?.type === 'checkbox' && Array.isArray(value)) {
    question.options.forEach((option) => {
      if (value.includes(option.value) && option.subQuestions) {
        option.subQuestions.forEach((subQuestion) => {
          subQuestionsMap[subQuestion.id.toString()] = subQuestion;
        });
      }
    });
  } else if (question?.type === 'radio' && typeof value === 'string') {
    question.options
      .find((option) => option.value === value)
      ?.subQuestions?.forEach((subQuestion) => {
        subQuestionsMap[subQuestion.id.toString()] = subQuestion;
      });
  }

  return subQuestionsMap;
};

export const transformQuestionnaireResponseToFormValues = ({
  questions,
  questionIdToNumberMap,
  assessmentsAnswersResponse,
}: {
  questions: AssessmentsQuestionViewModels;
  questionIdToNumberMap: QuestionIdToQuestionNumberMap;
  assessmentsAnswersResponse: AssessmentsAnswersResponse | undefined;
}): FieldValues => {
  const formValues = assessmentsAnswersResponse?.answers?.item?.reduce((acc, responseItem) => {
    const { linkId, answer } = responseItem;
    const question =
      questionIdToNumberMap[linkId] !== undefined
        ? questions[questionIdToNumberMap[linkId] - 1]
        : undefined;

    const formValue = answerToFormValue({
      question,
      responseItem,
    });

    if (formValue !== undefined) {
      acc[linkId] = formValue;
    }

    const subQuestions = getSubQuestionsMap(question, formValue);

    if (question?.type === 'checkbox' || question?.type === 'radio') {
      // Per the API contract, sub-question answers are nested under the item
      // array of the specific selected answer (answer[].item), not on the
      // parent response item itself.
      answer?.forEach((selectedAnswer) => {
        selectedAnswer.item?.forEach((subResponseItem) => {
          const subQuestion = subQuestions[subResponseItem.linkId];

          if (subQuestion && subResponseItem.answer) {
            const subAnswerValue = answerToFormValue({
              question: subQuestion,
              responseItem: subResponseItem,
            });

            if (subAnswerValue !== undefined) {
              acc[subQuestion.id.toString()] = subAnswerValue;
            }
          }
        });
      });
    }

    return acc;
  }, {} as FieldValues);

  return formValues ?? {};
};
